import { ChangeFunctionType } from '@bookjane2/bookjane-design-library/lib/common.types';
import {
  filterPayCodesByTitle,
  PayCodeOptionType,
  payCodeToOption,
  usePayCodes,
} from 'components/PayCodeSelectInput';
import { useMemo } from 'react';
import { TimeOffTitleEnum } from 'types/graphql';

export interface SavedTimeOffPayCode {
  id: number;
  name: string;
  timeOffTitle: TimeOffTitleEnum;
}

export function useTimeOffPayCodes({
  title,
  payCodeId,
  onChange,
  savedPayCode,
  isEnabled = true,
}: {
  title: string[];
  payCodeId: string[];
  onChange: ChangeFunctionType;
  savedPayCode?: SavedTimeOffPayCode | null;
  isEnabled?: boolean;
}): {
  payCodeOptions: PayCodeOptionType[];
  handleTitleChange: ChangeFunctionType;
  isPayCodeBlockingSubmit: boolean;
} {
  const { payCodes, isLoading } = usePayCodes(isEnabled);

  const payCodeOptions = useMemo(() => {
    const options = filterPayCodesByTitle(payCodes, title[0] as TimeOffTitleEnum).map(
      payCodeToOption,
    );
    const isSavedPayCodeMissing =
      savedPayCode &&
      savedPayCode.timeOffTitle === title[0] &&
      !options.some((option) => option.value === `${savedPayCode.id}`);
    if (!isSavedPayCodeMissing) return options;
    return [
      payCodeToOption({
        id: savedPayCode.id,
        display_text: savedPayCode.name,
        time_off_title: savedPayCode.timeOffTitle,
      }),
      ...options,
    ];
  }, [payCodes, title, savedPayCode]);

  const handleTitleChange: ChangeFunctionType = (event) => {
    const events = Array.isArray(event) ? event : [event];
    const titleEvent = events.find((e) => e.target.name === 'title');
    const nextTitle = Array.isArray(titleEvent?.target.value)
      ? titleEvent?.target.value[0]
      : titleEvent?.target.value;
    if (nextTitle === title[0]) return onChange(event);
    const nextPayCodeId =
      savedPayCode && nextTitle === savedPayCode.timeOffTitle ? [`${savedPayCode.id}`] : [];
    onChange([...events, { target: { name: 'pay_code_id', value: nextPayCodeId } }]);
  };

  return {
    payCodeOptions,
    handleTitleChange,
    isPayCodeBlockingSubmit: isLoading || (payCodeOptions.length > 0 && !payCodeId.length),
  };
}
